<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from './stores/authStore';
import AppHeader from './components/layout/AppHeader.vue';
import ActorFilterPills from './components/layout/ActorFilterPills.vue';
import NowPlayingBar from './components/layout/NowPlayingBar.vue';
import BottomNav from './components/layout/BottomNav.vue';
import AudioEffectsPanel from './components/layout/AudioEffectsPanel.vue';
import PanicStopButton from './components/PanicStopButton.vue';
import AppFooter from './components/layout/AppFooter.vue';

const authStore = useAuthStore();
const route = useRoute();
const showEffectsPanel = ref(false);

// Show footer on info pages but not on the main soundboard grid
const showFooter = computed(() => {
  const infoPages = ['privacy', 'terms', 'about', 'contact'];
  return infoPages.includes(route.name as string);
});

// Show filters only on main pages
const showFilters = computed(() => {
  const mainPages = ['home', 'favorites'];
  return mainPages.includes(route.name as string);
});

onMounted(async () => {
  // Try to fetch current user on app load (check if session is valid)
  if (authStore.user) {
    try {
      await authStore.fetchCurrentUser();
    } catch {
      // Session expired
    }
  }
});

function openEffectsPanel() {
  showEffectsPanel.value = true;
}

function closeEffectsPanel() {
  showEffectsPanel.value = false;
}
</script>

<template>
  <div class="min-h-screen flex flex-col overflow-hidden">
    <!-- Header -->
    <AppHeader />

    <!-- Filters (Sticky) - only on main pages -->
    <div v-if="showFilters" class="sticky top-0 z-40 bg-background-dark/80 backdrop-blur-md border-b border-console-border/50 px-4 md:px-8 py-3">
      <div class="max-w-7xl mx-auto">
        <ActorFilterPills />
      </div>
    </div>

    <!-- Main Content -->
    <main class="flex-1 overflow-y-auto px-4 md:px-8 py-6 pb-40">
      <router-view />
      
      <!-- Content Footer (on info pages) -->
      <AppFooter v-if="showFooter" />
    </main>

    <!-- Panic Stop Button -->
    <PanicStopButton />

    <!-- Footer / Now Playing Bar -->
    <footer class="fixed bottom-0 w-full bg-background-dark/95 backdrop-blur-xl border-t border-console-border z-50">
      <NowPlayingBar />
      <BottomNav @open-effects="openEffectsPanel" />
    </footer>

    <!-- Effects Panel (Mobile Drawer) -->
    <AudioEffectsPanel :is-open="showEffectsPanel" @close="closeEffectsPanel" />
  </div>
</template>

